import httpClient, { handleAPIError, createSuccessResponse } from './httpClient.js';
import { apiRetryManager } from '../retryManager.js';

/**
 * CountryAPI - Handles all country-related API operations
 *
 * Provides endpoints for searching countries, fetching country bounds,
 * and retrieving country details
 */
export class CountryAPI {
  constructor() {
    this.client = httpClient;
  }

  /**
   * Search countries by name (for search suggestions)
   *
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @param {number} options.limit - Maximum number of results (default: 5)
   * @returns {Promise<Object>} Response with formatted country results
   */
  async searchCountries(query, options = {}) {
    const context = {
      operation: 'searchCountries',
      query: query
    };

    try {
      return await apiRetryManager.retrySearch(async () => {
        const params = {
          q: query,
          limit: options.limit || 5
        };

        const response = await this.client.get('/countries/search', { params });

        // Response already includes formatted results from API
        return createSuccessResponse({
          results: response.data.results || [],
          meta: response.data.meta
        }, response.status);
      }, context);
    } catch (error) {
      return handleAPIError(error, context);
    }
  }

  /**
   * Get country bounding box by identifier
   *
   * @param {string|number} identifier - Country ID, name, ISO2, or ISO3
   * @returns {Promise<Object>} Response with country bounds data
   */
  async getCountryBounds(identifier) {
    const context = {
      operation: 'getCountryBounds',
      identifier: identifier
    };

    try {
      const response = await this.client.get(`/countries/${identifier}/bounds`);
      return createSuccessResponse(response.data, response.status);
    } catch (error) {
      return handleAPIError(error, context);
    }
  }

  /**
   * Get detailed country information
   *
   * @param {string|number} identifier - Country ID, name, ISO2, or ISO3
   * @returns {Promise<Object>} Response with country details
   */
  async getCountryDetails(identifier) {
    const context = {
      operation: 'getCountryDetails',
      identifier: identifier
    };

    try {
      const response = await this.client.get(`/countries/${identifier}`);
      return createSuccessResponse(response.data, response.status);
    } catch (error) {
      return handleAPIError(error, context);
    }
  }

  /**
   * Get list of all countries
   *
   * @param {Object} options - Query options
   * @param {boolean} options.include_bounds - Include bbox data
   * @param {boolean} options.has_mines - Only countries with mines
   * @returns {Promise<Object>} Response with countries list
   */
  async getCountries(options = {}) {
    const context = {
      operation: 'getCountries',
      options: options
    };

    try {
      const response = await this.client.get('/countries', {
        params: options
      });
      return createSuccessResponse(response.data, response.status);
    } catch (error) {
      return handleAPIError(error, context);
    }
  }
}

// Export singleton instance
export const countryAPI = new CountryAPI();
